/**
 * SingularityPassiveSkillEffect — Astral rift-path passive: death explosion
 * inside Event Horizon.
 *
 * When an enemy dies inside an active Event Horizon zone, rolls a 30% chance
 * for a detonation explosion that deals 10 damage to all enemies within 1 tile
 * of the death position.
 *
 * The external combat/death system calls `handleEnemyDeath(x, y)` for every
 * enemy kill. This passive checks zone membership via
 * `ZoneSystem.isInsideType(x, y, 'event_horizon')` and runs the probability
 * roll. On success, queries nearby enemies and deals burst damage.
 *
 * Apotheosis parent: one of the two OR-gate parents for
 * `apotheosis_event_horizon` (Dead Star).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § event_horizon_passive
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SingularityDeps {
  readonly zones: {
    isInsideType(x: number, y: number, zoneType: string): boolean;
  };
  readonly enemies: {
    getEnemiesInRadius(x: number, y: number, radiusTiles: number): string[];
  };
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  /** Deterministic-seeable random number generator (returns 0–1). */
  readonly rng: {
    random(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SingularityConfig {
  readonly detonationChance: number;
  readonly detonationDamage: number;
  readonly detonationRadiusTiles: number;
}

const DEFAULT_CONFIG: SingularityConfig = {
  detonationChance: 0.3,
  detonationDamage: 10,
  detonationRadiusTiles: 1,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SingularityPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'event_horizon_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SingularityDeps;
  readonly config: SingularityConfig;

  private _explosionCount = 0;
  private _deathsChecked = 0;

  constructor(deps: SingularityDeps, config: Partial<SingularityConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get explosionCount(): number { return this._explosionCount; }
  get deathsChecked(): number { return this._deathsChecked; }

  // ---------------------------------------------------------------------------
  // Public API — called by combat/death systems
  // ---------------------------------------------------------------------------

  /**
   * Called by the entity death system when an enemy is killed.
   * If the kill position is inside an active Event Horizon zone, rolls for
   * a detonation explosion.
   */
  handleEnemyDeath(deathX: number, deathY: number): void {
    this._deathsChecked++;

    // Only detonate if the kill occurred inside an Event Horizon zone.
    if (!this.deps.zones.isInsideType(deathX, deathY, 'event_horizon')) return;

    // 30% chance per eligible kill.
    if (this.deps.rng.random() >= this.config.detonationChance) return;

    // Explode: damage all enemies within detonationRadiusTiles of death point.
    const targets = this.deps.enemies.getEnemiesInRadius(
      deathX, deathY, this.config.detonationRadiusTiles,
    );
    for (const enemyId of targets) {
      this.deps.combat.dealDamage(enemyId, this.config.detonationDamage);
    }
    this._explosionCount++;
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — nothing to tick */ }
  destroy(): void { /* no state to clean up */ }
}
